import { useTranslation } from '@fluxora/i18n';
import { Tab, TabList, TabPanel, Tabs } from '@fluxora/ui';

import { FUNNEL_METHODS, funnelMethodLabelKey, type FunnelMethod } from '../services/funnelMethod';
import { EsiidSubflow } from './EsiidSubflow';
import { ZipSubflow } from './ZipSubflow';

/**
 * The meter-first landing method chooser (§3 of the epic).
 *
 * Two tabs, one panel each: "Shop by ESI ID" and "Shop by ZIP". Selection is
 * a controlled Tabs component — the parent owns the current method (which
 * lives in `FunnelProvider`) so switching tabs never resets the ZIP the
 * shopper already typed, and the resolved meter card can persist across a
 * tab switch downstream.
 *
 * ── LOGIC OUT OF THE JSX ───────────────────────────────────────────────────
 * The tab tokens (`'esiid' | 'zip'`) and their i18n label keys live in
 * `services/funnelMethod.ts`. This component only renders. Sub-flow contents
 * are their own components (`ZipSubflow`, `EsiidSubflow`) — the chooser is a
 * dumb switchboard.
 */
export interface MethodChooserProps {
  readonly value: FunnelMethod;
  readonly onChange: (method: FunnelMethod) => void;
}

export function MethodChooser({ value, onChange }: MethodChooserProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <Tabs value={value} onValueChange={(v) => onChange(v as FunnelMethod)}>
      <TabList
        aria-label={t('shop.landing.method.tabsAria')}
        className="justify-center rounded-full bg-surface p-1 shadow-card"
      >
        {FUNNEL_METHODS.map((method) => (
          <Tab key={method} value={method}>
            {t(funnelMethodLabelKey(method))}
          </Tab>
        ))}
      </TabList>

      <TabPanel value="esiid" className="pt-4">
        <EsiidSubflow />
      </TabPanel>
      <TabPanel value="zip" className="pt-4">
        <ZipSubflow />
      </TabPanel>
    </Tabs>
  );
}
